import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { EventBus } from '../core/EventBus';
import type { Camera } from '../renderer/Camera';
import type { CameraConfig } from '../config/CameraConfig';
import { CameraInput, type CameraInputHandler } from './CameraInput';
import { CameraTransition } from './CameraTransition';
import { pitchForMode } from './CameraPitch';
import { enterFlightView, exitFlightView } from './CameraFlightView';
import {
  createPlanetHubState, previewPlanet, cancelPreview,
  focusPlanetHub, restoreFromHub, type PlanetHubState,
} from './PlanetHubCamera';
import { CameraMode } from './CameraMode';
export { CameraMode };

export class CameraController implements GameModule, CameraInputHandler {
  readonly name = 'CameraController';
  _camera: Camera;
  _config: CameraConfig;
  _events!: EventBus;
  private _input!: CameraInput;
  private _canvas!: HTMLCanvasElement;
  _mode = CameraMode.Free;
  private _dragging = false;
  _trans = new CameraTransition();
  _zoom: number;
  _zoomTarget: number;
  private _sinPitch = 1;
  _yaw = 0;
  private _velocityX = 0;
  private _velocityZ = 0;
  private _followSrc: { centerX: number; centerY: number; centerZ: number; active: boolean } | null = null;
  _savedZoom = 0;
  _savedTargetX = 0;
  _savedTargetZ = 0;
  private _hexRadius: number;
  private _hubState: PlanetHubState = createPlanetHubState();

  constructor(camera: Camera, config: CameraConfig, hexRadius: number) {
    this._camera = camera;
    this._config = config;
    this._hexRadius = hexRadius;
    this._zoom = config.zoomDefault;
    this._zoomTarget = config.zoomDefault;
  }

  get mode(): CameraMode { return this._mode; }
  get zoom(): number { return this._zoom; }
  set zoomTarget(v: number) { this._zoomTarget = v; }

  setFollowSource(src: { centerX: number; centerY: number; centerZ: number; active: boolean }): void {
    this._followSrc = src;
  }

  init(engine: Engine): void {
    this._events = engine.events;
    this._canvas = engine.canvas;
    this._input = new CameraInput(engine.canvas, this);
    this._events.on<null>('fleet:enter-flightview', () => enterFlightView(this));
    this._events.on<null>('fleet:exit-flightview', () => exitFlightView(this));
    this._events.on<{ axialQ: number; axialR: number }>('planet:focus-camera', (d) =>
      this._focusPlanet(d.axialQ, d.axialR),
    );
    this._events.on<{ x: number; z: number }>('stress:focus-camera', (d) => {
      this._velocityX = 0; this._velocityZ = 0;
      this._camera.target.set(d.x, 0, d.z);
    });
    this._events.on<{ x: number; z: number }>('fleet:focus-travel', (d) => {
      this._velocityX = 0; this._velocityZ = 0;
      this._camera.target.set(d.x, 0, d.z);
    });
    this._events.on<null>('planet:hub-closed', () => this._restoreFromHub());
    this._events.on<{ axialQ: number; axialR: number; panelFraction?: number }>('camera:preview-planet', (d) =>
      this._previewPlanet(d.axialQ, d.axialR, d.panelFraction ?? 0),
    );
    this._events.on<null>('camera:preview-cancel', () => this._cancelPreview());
    this._applyCamera();
  }

  update(dt: number): void {
    const cam = this._camera;
    if (this._trans.active) {
      const e = this._trans.advance(dt, this._config.flightView.transitionSpeed);
      let endX: number, endY: number, endZ: number;
      if (this._hubState.focusTarget) {
        endX = this._hubState.focusTarget.x; endY = 0; endZ = this._hubState.focusTarget.z;
      } else if (this._mode === CameraMode.FlightView && this._followSrc) {
        endX = this._followSrc.centerX; endY = this._followSrc.centerY; endZ = this._followSrc.centerZ;
      } else {
        endX = this._savedTargetX; endY = 0; endZ = this._savedTargetZ;
      }
      this._trans.interpolateTarget(cam, endX, endY, endZ, e);
      this._zoom = this._trans.interpolateZoom(this._zoomTarget, e);
    } else if (this._mode === CameraMode.FlightView && this._followSrc) {
      cam.target.x = this._followSrc.centerX;
      cam.target.y = this._followSrc.centerY;
      cam.target.z = this._followSrc.centerZ;
      this._zoom += (this._zoomTarget - this._zoom) * this._config.zoomSmooth;
    } else {
      if (!this._dragging) {
        cam.target.x += this._velocityX;
        cam.target.z += this._velocityZ;
        this._velocityX *= this._config.panDamping;
        this._velocityZ *= this._config.panDamping;
      }
      this._zoom += (this._zoomTarget - this._zoom) * this._config.zoomSmooth;
    }
    this._applyCamera();
  }

  dispose(): void { this._input.dispose(); }

  onDragStart(): void {
    this._dragging = true;
    this._velocityX = 0; this._velocityZ = 0;
  }

  onDrag(dx: number, dz: number): void {
    if (!this._dragging) return;
    if (this._mode === CameraMode.FlightView) {
      this._yaw -= dx * 0.005;
    } else {
      const scale = this._zoom / this._canvas.clientHeight * 1.8;
      const wx = -dx * scale;
      const wz = -dz * (scale / this._sinPitch);
      this._camera.target.x += wx;
      this._camera.target.z += wz;
      this._velocityX = wx;
      this._velocityZ = wz;
    }
  }

  onDragEnd(): void { this._dragging = false; }

  onZoom(delta: number): void {
    const fv = this._mode === CameraMode.FlightView;
    const min = fv ? this._config.flightView.zoomMin : this._config.zoomMin;
    const max = fv ? this._config.flightView.zoomMax : this._config.zoomMax;
    this._zoomTarget += delta * (fv ? 0.002 : 0.08);
    this._zoomTarget = Math.max(min, Math.min(max, this._zoomTarget));
  }

  private _previewPlanet(axialQ: number, axialR: number, panelFraction: number): void {
    this._velocityX = 0; this._velocityZ = 0;
    let offsetX = 0;
    if (panelFraction > 0) {
      const aspect = this._canvas.clientWidth / this._canvas.clientHeight;
      const halfFov = this._config.fov / 2;
      offsetX = panelFraction * this._config.zoomMin * Math.tan(halfFov) * aspect;
    }
    this._zoomTarget = previewPlanet(
      axialQ, axialR, this._hexRadius,
      this._camera, this._config, this._trans, this._hubState, this._zoomTarget, offsetX,
    );
  }

  private _cancelPreview(): void {
    cancelPreview(this._camera, this._config, this._trans, this._hubState, this._zoom);
    this._savedTargetX = this._hubState.savedX;
    this._savedTargetZ = this._hubState.savedZ;
    this._zoomTarget = this._hubState.savedZoom;
  }

  private _focusPlanet(axialQ: number, axialR: number): void {
    this._velocityX = 0; this._velocityZ = 0; this._yaw = 0;
    this._zoomTarget = focusPlanetHub(
      axialQ, axialR, this._hexRadius,
      this._camera, this._config, this._trans, this._hubState, this._zoomTarget,
    );
  }

  private _restoreFromHub(): void {
    restoreFromHub(this._camera, this._config, this._trans, this._hubState, this._zoom);
    this._savedTargetX = this._hubState.savedX;
    this._savedTargetZ = this._hubState.savedZ;
    this._zoomTarget = this._hubState.savedZoom;
  }

  private _applyCamera(): void {
    const pitch = this._trans.active
      ? this._trans.currentPitch()
      : pitchForMode(this._mode, this._zoom, this._config);
    this._sinPitch = Math.sin(pitch);
    const height = this._zoom * this._sinPitch;
    const back = this._zoom * Math.cos(pitch);
    const cam = this._camera;
    cam.position.x = cam.target.x + back * Math.sin(this._yaw);
    cam.position.y = cam.target.y + height;
    cam.position.z = cam.target.z + back * Math.cos(this._yaw);
    cam.yaw = this._yaw;
    cam.pitch = pitch;
  }
}
